import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import './DashboardScore.css';
import CreateEvent from './components/CreateEvent';
import EditEvent from './components/EditEvent';
import EventCard from './components/EventCard';
import EditShooters from './components/EditShooters';
import ScoreSheetManagement from './components/ScoreSheetManagement';
import FunShootQuickLaunch from './components/FunShootQuickLaunch';
import OwnershipTransferInbox from './components/OwnershipTransferInbox';
import { Plus, Target, Calendar, X, CheckCircle } from 'lucide-react';
import { eventsService } from '../../../../../services';

const resolveEventType = (value) => {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'rifle') return 'Rifle';
    if (normalized === 'game') return 'Game';
    return 'Clay';
};

const DashboardScore = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const highlightedEventRef = useRef(null);

    const [events, setEvents] = useState([]);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [editEventModalOpen, setEditEventModalOpen] = useState(false);
    const [editShootersModalOpen, setEditShootersModalOpen] = useState(false);
    const [scoreSheetManagementOpen, setScoreSheetManagementOpen] = useState(false);
    const [funShootQuickLaunchOpen, setFunShootQuickLaunchOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [editEventInitialView, setEditEventInitialView] = useState('details');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [highlightedEventId, setHighlightedEventId] = useState(null);
    // Add a refreshKey to force re-mounting of components
    const [shootersRefreshKey, setShootersRefreshKey] = useState(0);
    const [eventTypeFilter, setEventTypeFilter] = useState('All');
    const [ownershipTransfers, setOwnershipTransfers] = useState({ incoming: [], outgoing: [] });

    useEffect(() => {
        const requestedType = resolveEventType(searchParams.get('type'));
        const hasTypeParam = Boolean(searchParams.get('type'));
        setEventTypeFilter(hasTypeParam ? requestedType : 'All');
    }, [searchParams]);

    const fetchEvents = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await eventsService.getEvents();
            setEvents(data);
        } catch (error) {
            console.error('Error fetching events:', error);
            setError('Failed to load events. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchEvents();
    }, [fetchEvents]);

    const fetchOwnershipTransfers = useCallback(async () => {
        try {
            const data = await eventsService.getOwnershipTransfers();
            setOwnershipTransfers({
                incoming: Array.isArray(data?.incoming) ? data.incoming : [],
                outgoing: Array.isArray(data?.outgoing) ? data.outgoing : []
            });
        } catch (transferError) {
            console.error('Error fetching ownership transfers:', transferError);
        }
    }, []);

    useEffect(() => {
        fetchOwnershipTransfers();
    }, [fetchOwnershipTransfers]);

    // Handle eventId from URL query params - scroll to and highlight the event
    useEffect(() => {
        const eventId = searchParams.get('eventId');
        if (eventId && events.length > 0) {
            const numericEventId = parseInt(eventId, 10);
            setHighlightedEventId(numericEventId);
            
            // Scroll to the event after a brief delay to allow render
            setTimeout(() => {
                if (highlightedEventRef.current) {
                    highlightedEventRef.current.scrollIntoView({ 
                        behavior: 'smooth', 
                        block: 'center' 
                    });
                }
            }, 100);
            
            // Clear the query param after handling and remove highlight after 3 seconds
            setTimeout(() => {
                setHighlightedEventId(null);
                const nextParams = new URLSearchParams(searchParams);
                nextParams.delete('eventId');
                setSearchParams(nextParams, { replace: true });
            }, 3000);
        }
    }, [searchParams, events, setSearchParams]);

    const closeCreateModal = () => setCreateModalOpen(false);
    const closeEditEventModal = () => setEditEventModalOpen(false);

    const openEditEventModal = (event, initialView = 'details') => {
        setSelectedEvent(event);
        setEditEventInitialView(initialView);
        setEditEventModalOpen(true);
    };

    const closeEditShootersModal = () => {
        setEditShootersModalOpen(false);
        // Increment refresh key when closing edit shooters modal to force refresh
        setShootersRefreshKey(prevKey => prevKey + 1);
    };

    const openEditShootersModal = (event) => {
        setSelectedEvent(event);
        setEditShootersModalOpen(true);
    };

    const openScoreSheetManagement = (event) => {
        setSelectedEvent(event);
        setScoreSheetManagementOpen(true);
    };

    const closeScoreSheetManagement = () => setScoreSheetManagementOpen(false);
    const closeFunShootQuickLaunch = () => setFunShootQuickLaunchOpen(false);
    const openFunShootQuickLaunch = (event) => {
        setSelectedEvent(event);
        setFunShootQuickLaunchOpen(true);
    };

    const handleCreateEvent = (newEvent) => {
        setEvents(prevEvents => [newEvent, ...prevEvents]);
        closeCreateModal();
    };

    const handleUpdateEvent = async (updatedEvent) => {
        try {
            const updatedEventData = await eventsService.updateEvent(updatedEvent.eventId, updatedEvent);

            setEvents(prevEvents =>
                prevEvents.map(event =>
                    event.eventId === updatedEventData.eventId ? updatedEventData : event
                )
            );

            closeEditEventModal();
        } catch (error) {
            console.error('Error updating event:', error);
            setError('Failed to update event. Please try again.');
        }
    };

    const handleDeleteEvent = async (eventId) => {
        const event = events.find(e => e.eventId === eventId);
        if (window.confirm(`Are you sure you want to delete the event "${event?.name}"?`)) {
            try {
                setError(null); // Clear any existing errors
                setIsLoading(true);
                await eventsService.deleteEvent(eventId);

                // Remove the event from state
                await fetchEvents();

                // Show success message
                const successMessage = `Event "${event?.name}" has been successfully deleted`;
                setError({ message: successMessage, type: 'success' });

                // Clear success message after 3 seconds
                setTimeout(() => {
                    setError(null);
                }, 3000);

            } catch (error) {
                console.error('Error deleting event:', error);
                setError({ message: 'Failed to delete event. Please try again.', type: 'error' });
            } finally {
                setIsLoading(false);
            }
        }
    };

    // Callback for EditShooters to notify parent about shooter changes
    const handleShootersUpdated = useCallback(() => {
        setShootersRefreshKey(prevKey => prevKey + 1);
    }, []);

    const handleOwnershipTransfersChanged = useCallback(async () => {
        await Promise.all([fetchEvents(), fetchOwnershipTransfers()]);
    }, [fetchEvents, fetchOwnershipTransfers]);

    // Filter events by type
    const filteredEvents = eventTypeFilter === 'All'
        ? events
        : events.filter(event => resolveEventType(event.eventType ?? event.EventType) === eventTypeFilter);

    return (
        <div className='dashboard-component score-events-page'>
            {/* Header */}
            <div className="score-events-header">
                <div className="score-events-header-content">
                    <div className="score-events-header-icon">
                        <Target size={28} />
                    </div>
                    <div className="score-events-header-text">
                        <h1>Score Events</h1>
                        <p>Track your shoots, competitions, and training sessions</p>
                    </div>
                </div>
                <button className="score-events-create-btn" onClick={() => setCreateModalOpen(true)}>
                    <Plus size={20} />
                    <span>Create Event</span>
                </button>
            </div>

            {/* Stats Overview */}
            <div className="score-events-stats">
                <div className="score-events-stat">
                    <div className="score-events-stat-icon orange">
                        <Calendar size={20} />
                    </div>
                    <div className="score-events-stat-info">
                        <span className="score-events-stat-value">{events.length}</span>
                        <span className="score-events-stat-label">Total Events</span>
                    </div>
                </div>
            </div>

            {/* Event Type Filter Tabs */}
            <div className="score-events-filter-tabs">
                {['All', 'Clay', 'Rifle', 'Game'].map(type => (
                    <button
                        key={type}
                        className={`filter-tab ${eventTypeFilter === type ? 'active' : ''}`}
                        onClick={() => {
                            setEventTypeFilter(type);
                            if (type === 'All') {
                                const nextParams = new URLSearchParams(searchParams);
                                nextParams.delete('type');
                                setSearchParams(nextParams, { replace: true });
                                return;
                            }

                            const nextParams = new URLSearchParams(searchParams);
                            nextParams.set('type', type);
                            setSearchParams(nextParams, { replace: true });
                        }}
                    >
                        {type}
                    </button>
                ))}
            </div>

            {/* Loading State */}
            {isLoading && (
                <div className="score-events-loading">
                    <div className="score-events-spinner"></div>
                    <p>Loading events...</p>
                </div>
            )}

            {/* Error/Success Messages */}
            {error && (
                <div className={`score-events-toast ${error.type === 'success' ? 'toast-success' : 'toast-error'}`}>
                    {error.type === 'success' ? <CheckCircle size={18} /> : <X size={18} />}
                    <span>{error.message}</span>
                    <button onClick={() => setError(null)}><X size={16} /></button>
                </div>
            )}

            <OwnershipTransferInbox
                transfers={ownershipTransfers.incoming}
                onTransfersChanged={handleOwnershipTransfersChanged}
            />

            {/* Events Grid */}
            {!isLoading && filteredEvents.length > 0 && (
                <div className="score-events-grid">
                    {filteredEvents.map(event => (
                        <div 
                            key={event.eventId}
                            ref={event.eventId === highlightedEventId ? highlightedEventRef : null}
                            className={highlightedEventId === event.eventId ? 'event-card-highlight' : ''}
                        >
                            <EventCard
                                event={{
                                    id: event.eventId,
                                    name: event.name,
                                    eventTime: event.eventTime,
                                    eventDate: event.eventDate,
                                    clubName: event.clubName,
                                    clubId: event.clubId,
                                    eventType: resolveEventType(event.eventType ?? event.EventType),
                                    ownerName: event.ownerName,
                                    isOwner: event.isOwner,
                                    isShooter: event.isShooter,
                                    imageCount: event.imageCount || 0,
                                    coverImageUrl: event.coverImageUrl || null
                                }}
                                onEdit={() => openEditEventModal(event, 'details')}
                                onEditShooters={() => openEditShootersModal(event)}
                                onDelete={() => handleDeleteEvent(event.eventId)}
                                onManageScoreSheets={() => openScoreSheetManagement(event)}
                                onQuickFunShoot={() => openFunShootQuickLaunch(event)}
                                onManageMedia={() => openEditEventModal(event, 'media')}
                            />
                        </div>
                    ))}
                </div>
            )}

            {/* Empty State */}
            {!isLoading && filteredEvents.length === 0 && (
                <div className="score-events-empty">
                    <div className="score-events-empty-icon">
                        <Target size={48} />
                    </div>
                    <h3>No Events Yet</h3>
                    <p>Create your first event to start tracking your scores</p>
                    <button className="score-events-empty-btn" onClick={() => setCreateModalOpen(true)}>
                        <Plus size={18} />
                        Create Your First Event
                    </button>
                </div>
            )}

            <CreateEvent
                isOpen={createModalOpen}
                closeModal={closeCreateModal}
                onEventCreated={handleCreateEvent}
            />
            <EditEvent
                isOpen={editEventModalOpen}
                closeModal={closeEditEventModal}
                event={selectedEvent}
                onEventUpdated={handleUpdateEvent}
                initialView={editEventInitialView}
                canEditDetails={selectedEvent?.isOwner !== false}
                canManageMedia={selectedEvent?.isOwner !== false}
                onTransferChanged={handleOwnershipTransfersChanged}
            />
            <EditShooters
                isOpen={editShootersModalOpen}
                closeModal={closeEditShootersModal}
                event={selectedEvent}
                onShootersUpdated={handleShootersUpdated}
            />
            <ScoreSheetManagement
                eventId={selectedEvent?.eventId}
                isOpen={scoreSheetManagementOpen}
                onClose={closeScoreSheetManagement}
                refreshKey={shootersRefreshKey} // Pass the refresh key to force re-render
                canManage={selectedEvent?.isOwner !== false}
            />
            <FunShootQuickLaunch
                event={selectedEvent}
                isOpen={funShootQuickLaunchOpen}
                onClose={closeFunShootQuickLaunch}
            />
        </div>
    );
};

export default DashboardScore;
